/**
 * The particle point shader (SPEC §5.3, and CLAUDE.md's "no external assets").
 *
 * `THREE.PointsMaterial` draws each point as a flat, fully opaque square. At the ranges the
 * impact cam works at, a fireball is then a handful of hard-edged blocks — which is exactly what
 * it looked like. The usual remedy is a soft round sprite texture, and this project cannot have
 * one: every texture is generated in code. So the falloff is computed analytically from
 * `gl_PointCoord` instead, which costs a couple of instructions and needs no texture memory at
 * all.
 *
 * The shader also restores two things `PointsMaterial` silently dropped:
 *
 * - **Per-particle size.** The system computes a size ramp per particle (smoke and fire expand as
 *   they age) into a `size` attribute that `PointsMaterial` never reads, because it takes a single
 *   uniform size for the whole system. Every particle therefore drew at the same 8 units no matter
 *   what the simulation said.
 * - **Fade over life.** Particles previously vanished at full opacity the instant their lifetime
 *   expired, which reads as a pop rather than as smoke thinning out.
 *
 * Sizes are metres in world space; `gl_PointSize` is framebuffer pixels, so the vertex stage does
 * the perspective conversion itself — the renderer only supplies it for `PointsMaterial`.
 */

export const PARTICLE_VERTEX = /* glsl */ `
attribute float size;
/** x = life alpha 0–1, y = edge softness 0 (hard) to 1 (very soft). */
attribute vec2 aParams;

uniform float uViewportHeightPx;

varying vec3 vColour;
varying vec2 vParams;

void main() {
  vColour = color;
  vParams = aParams;

  vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);

  // World-space size to pixels. The projection matrix's [1][1] term is cot(fovY/2), so this is
  // the standard perspective point-size conversion and follows any zoom the camera applies.
  float depth = max(-viewPosition.z, 0.001);
  gl_PointSize = size * uViewportHeightPx * 0.5 * projectionMatrix[1][1] / depth;
  // A ceiling keeps a particle detonating next to the lens from becoming a full-screen quad, and
  // a floor keeps distant smoke from dropping below one pixel and shimmering.
  gl_PointSize = clamp(gl_PointSize, 1.0, 512.0);

  gl_Position = projectionMatrix * viewPosition;
}
`;

export const PARTICLE_FRAGMENT = /* glsl */ `
precision highp float;

varying vec3 vColour;
varying vec2 vParams;

void main() {
  // Distance from the centre of the point sprite, 0 at the middle and 1 at the inscribed circle.
  vec2 fromCentre = gl_PointCoord - vec2(0.5);
  float r = length(fromCentre) * 2.0;

  // Discard the corners outright rather than relying on alpha. Blending a fully transparent
  // corner still costs a fragment and, with depth writes off and hundreds of overlapping sprites,
  // still shows as a faint square edge where the blend accumulates.
  if (r > 1.0) discard;

  // Softness picks the profile: debris keeps a nearly hard rim because it is a solid fragment,
  // while smoke and fire fade most of the way across so they read as volume rather than as discs.
  float softness = clamp(vParams.y, 0.02, 1.0);
  float edge = smoothstep(1.0, 1.0 - softness, r);

  // A gentle centre boost so a soft particle still has a core; without it, heavy falloff makes a
  // fireball look like a smoke ring.
  float core = mix(1.0, 1.0 - r * r * 0.55, softness);

  float alpha = edge * core * vParams.x;
  if (alpha < 0.004) discard;

  gl_FragColor = vec4(vColour, alpha);
}
`;
